import { useMemo } from 'react';
import classNames from 'classnames';
import { Divider, Flex, Tag, Typography } from 'antd';
import { SnippetsOutlined } from '@ant-design/icons';
import UserAvatar from 'components/atoms/user-avatar';
import { alertStatusLabel } from 'components/organisms/oneplatform/patient-readings-section/types';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats, ReadingStatus } from 'infrastructure/enums';
import { languagesMap } from 'constants/languages';
import {
  type IPatientReading,
  type IPatientDataItem,
} from 'pages/homeDashboard/hooks/use-patient-readings';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'router/routes';
import { KEYBOARD_KEYS } from 'constants/keyboard';
import ProgramType from 'components/molecules/oneplaform/program-type';

import ThresholdsList from './thresholds-list';
import s from './styles.module.scss';
import ReadingsList from './readings-list';
import { formatReadingDate } from './patientReadingsUtils';

const { Text } = Typography;

interface IPatientReadingsProps {
  data: IPatientDataItem;
  onSnippetClick?: (patientGuid: string) => void;
}

const PatientReadingsAlert = ({
  data,
  onSnippetClick,
}: IPatientReadingsProps) => {
  const navigate = useNavigate();

  const { patient, readings } = data;
  const latestReading: IPatientReading = readings || ({} as IPatientReading);

  const formattedBirthDate = useMemo(() => {
    return DateFormatter({
      date: patient.birthDate || '',
      format: DateFormats['MM/dd/yyyy'],
    });
  }, [patient.birthDate]);

  const statusModifier = latestReading?.globalStatus?.toLowerCase();
  const statusClass = statusModifier
    ? s[`status-${statusModifier}`]
    : undefined;

  return (
    <Flex
      className={s.wrapper}
      align="flex-start"
      onClick={() => navigate(Routes.patientGuid(patient.guid))}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.SPACE) {
          e.preventDefault();
          navigate(Routes.patientGuid(patient.guid));
        }
      }}
      aria-label={`View patient details for ${patient.firstName} ${patient.lastName}`}
    >
      <UserAvatar
        firstName={patient.firstName || ''}
        lastName={patient.lastName || ''}
        size="default"
      />
      <Flex vertical flex="1">
        <Flex justify="space-between" align="flex-start">
          <Flex vertical>
            <Flex gap={10}>
              <Text className={s['patient-name-text']}>
                {patient.firstName || ''} {patient.lastName || ''}
              </Text>
              <ProgramType
                rpmStatus={patient.status}
                ccmStatus={patient.ccmStatus}
              />
            </Flex>
            <Text className={s['patient-info-text']}>
              {formattedBirthDate && (
                <>
                  {formattedBirthDate}
                  <Divider type="vertical" className={s.divider} />
                </>
              )}
              {patient.language && (
                <>
                  {languagesMap[patient.language] || patient.language}
                  <Divider type="vertical" className={s.divider} />
                </>
              )}
              {`${latestReading?.doctor?.title || ''} ${
                latestReading?.doctor?.firstName || ''
              } ${latestReading?.doctor?.lastName || ''}`}
            </Text>
          </Flex>
          {Object.keys(latestReading).length > 0 && (
            <Flex align="center" gap={4}>
              <Tag
                className={classNames(s['status-tag'], statusClass)}
                bordered={false}
              >
                {
                  alertStatusLabel[
                    latestReading?.globalStatus as unknown as ReadingStatus
                  ]
                }
              </Tag>
              <span
                className={s['status-action-button']}
                onClick={(e) => {
                  e.stopPropagation();
                  onSnippetClick?.(patient.guid);
                }}
                role="button"
                tabIndex={0}
                aria-label="Open key notes"
                onKeyDown={(e) => {
                  if (
                    e.key === KEYBOARD_KEYS.ENTER ||
                    e.key === KEYBOARD_KEYS.SPACE
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    onSnippetClick?.(patient.guid);
                  }
                }}
              >
                <SnippetsOutlined className={s['status-action-icon']} />
              </span>
            </Flex>
          )}
        </Flex>
        {Object.keys(latestReading).length > 0 && (
          <Flex className={s['reading-wrapper']} align="stretch" gap={8}>
            <ReadingsList
              id={latestReading.id}
              values={latestReading.value}
              statuses={latestReading.statuses}
            />
            {latestReading.globalStatus !== ReadingStatus.Normal.toString() && (
              <Divider
                type="vertical"
                className={s['reading-threshold-divider']}
              />
            )}
            <ThresholdsList
              id={latestReading.id}
              globalStatus={latestReading.globalStatus}
              values={latestReading.value}
              statuses={latestReading.statuses}
              readingType={latestReading.readingType}
              thresholds={patient.threshold}
            />
          </Flex>
        )}
        {Object.keys(latestReading).length > 0 && (
          <Text className={s['timestamp-text']}>
            {formatReadingDate(latestReading.dateTime)}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

export default PatientReadingsAlert;
